import { NavLink, useNavigate } from "react-router-dom";
import { BadgeCheck, CreditCard, LogOut, Sparkles, ShieldCheck } from "lucide-react";
import { BrandMonogram } from "@/components/BrandLogo";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard/inventory", label: "Inventory", testId: "nav-inventory" },
  { to: "/dashboard/requirements", label: "Requirements", testId: "nav-requirements" },
  { to: "/dashboard/matches", label: "Matches", testId: "nav-matches" },
  { to: "/dashboard/pipeline", label: "Pipeline", testId: "nav-pipeline" },
];

function initialsOf(name) {
  if (!name) return "MB";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");
}

export function AppHeader({ variant = "broker" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <header
      className="sticky top-0 z-40 border-b border-gray-200/70 bg-white/80 backdrop-blur-xl"
      data-testid="app-header"
    >
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-6 md:px-10">
        <div className="flex items-center gap-10">
          <NavLink to={variant === "admin" ? "/control-panel-x9" : "/dashboard"} className="mb-focus-ring flex items-center gap-2.5 rounded-lg">
            <BrandMonogram className="h-8" data-testid="header-brand-monogram" />
            <span
              className="hidden text-base font-bold tracking-tight md:inline"
              data-testid="header-brand-text"
            >
              <span className="text-brand-green">Match</span>
              <span className="text-brand-navy">Broker</span>
            </span>
            {variant === "admin" && (
              <span
                className="ml-1 inline-flex items-center gap-1 rounded-full border border-brand-navy/20 bg-brand-navy/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-navy"
                data-testid="admin-badge"
              >
                <ShieldCheck className="h-3 w-3" />
                Admin
              </span>
            )}
          </NavLink>
          {variant === "broker" && (
            <nav className="hidden items-center gap-1 md:flex" data-testid="primary-nav">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    cn(
                      "mb-transition rounded-full px-4 py-2 text-sm font-medium",
                      isActive
                        ? "bg-brand-navy text-white shadow-sm"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          )}
        </div>

        <div className="flex items-center gap-3">
          {variant === "broker" && <NotificationBell />}
          <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="mb-focus-ring mb-transition flex items-center gap-3 rounded-full border border-gray-200 bg-white px-3 py-1.5 hover:shadow-sm"
              data-testid="profile-menu-trigger"
            >
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-navy text-xs font-semibold text-white"
                aria-hidden
              >
                {initialsOf(user?.full_name)}
              </span>
              <div className="hidden text-left md:block">
                <div className="text-sm font-semibold leading-tight text-gray-900">
                  {user?.full_name || "Broker"}
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  {user?.is_verified ? (
                    <>
                      <BadgeCheck className="h-3.5 w-3.5 text-brand-green" />
                      <span data-testid="profile-verified-label">Verified</span>
                    </>
                  ) : (
                    <span>Pending review</span>
                  )}
                  {(user?.subscription_status === "pro" ||
                    user?.trial?.plan === "pro") && (
                    <span
                      className="ml-1 inline-flex items-center gap-1 rounded-full border border-brand-green/30 bg-brand-green/10 px-1.5 py-0 text-[10px] font-bold text-brand-green"
                      data-testid="profile-pro-badge"
                    >
                      <Sparkles className="h-2.5 w-2.5" />
                      PRO
                    </span>
                  )}
                  {user?.trial?.plan === "trialing" && (
                    <span
                      className="ml-1 inline-flex items-center gap-1 rounded-full border border-brand-navy/30 bg-brand-navy/5 px-1.5 py-0 text-[10px] font-bold text-brand-navy"
                      data-testid="profile-trial-badge"
                    >
                      TRIAL
                    </span>
                  )}
                  {user?.trial?.plan === "trial_expired" && (
                    <span
                      className="ml-1 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] font-bold text-amber-700"
                      data-testid="profile-trial-expired-badge"
                    >
                      TRIAL ENDED
                    </span>
                  )}
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs uppercase tracking-wider text-gray-400">
              Signed in as
            </DropdownMenuLabel>
            <DropdownMenuLabel
              className="pt-0 font-normal text-gray-700"
              data-testid="profile-menu-email"
            >
              {user?.email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {variant !== "admin" && (
              <>
                <DropdownMenuItem asChild>
                  <NavLink
                    to="/dashboard/billing"
                    className="flex items-center gap-2 text-gray-700 focus:bg-gray-100"
                    data-testid="profile-menu-billing"
                  >
                    <CreditCard className="h-4 w-4" />
                    Billing
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              className="flex items-center gap-2 text-gray-700 focus:bg-gray-100"
              onSelect={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
